// ── Template resolver (import side) ──────────────────────────────────────────
// Turns a de-identified v2 template into a live ServerDashboardPayload for the
// importing team: metrics resolve identity-keep-first then category-aware
// positional; subjects resolve positionally against the roster; unresolvable
// (empty-category) widgets are dropped. See Docs/TEMPLATE_SYSTEM.md §5–§7.

import type { ComponentSpec } from "../Processing/types";
import type { GridWidgetType } from "../types";
import type { ServerDashboardPayload } from "../Contexts/dashboardState";
import { getWidgetSlots, isNoSlots } from "../Slots";
import {
  TOKEN_RE,
  parseToken,
  SUBJECT_ID_PREFIX,
  SUBJECT_KIND_BY_PREFIX,
  type MetricCategory,
  type SubjectKind,
  type TemplateFileV2,
  type TemplateResolveContext,
  type ResolutionReport,
} from "./templateTokens";

interface MetricBinding { id: string; name: string }
interface SubjectBinding { id: string; name: string }

export interface ResolveResult {
  payload: ServerDashboardPayload;
  report: ResolutionReport;
}

export function resolveTemplateV2(
  template: TemplateFileV2,
  ctx: TemplateResolveContext,
): ResolveResult {
  const report: ResolutionReport = { substitutions: [], droppedWidgetIds: [] };

  const metricBindings = bindMetrics(template, ctx, report);
  const subjectBindings = bindSubjects(template, ctx, report);

  // token body ("MET_1", "PL_2", …) → resolved display name, for the global name pass.
  const nameByToken = new Map<string, string>();
  for (const [tok, b] of metricBindings) if (b) nameByToken.set(tok, b.name);
  for (const [tok, b] of subjectBindings) nameByToken.set(tok, b.name);

  const keptWidgets: ServerDashboardPayload["widgets"] = [];
  const keptIds = new Set<string>();

  for (const w of template.widgets) {
    const config = JSON.parse(JSON.stringify(w.config)) as Record<string, unknown>;
    const descriptor = getWidgetSlots(w.type as GridWidgetType);
    let drop = false;

    if (descriptor && !isNoSlots(descriptor)) {
      const spec = config as unknown as ComponentSpec;

      for (const ref of descriptor.metricRefs(spec)) {
        const tok = parseTokenBody(ref.get());
        if (!tok) continue;
        const binding = metricBindings.get(tok);
        if (binding) ref.set(binding.id);
        else { drop = true; } // unresolved (empty category) → drop the widget
      }

      for (const ref of descriptor.subjectRefs(spec)) {
        const raw = ref.get();
        if (raw == null || raw === "full-team") continue;
        const tok = parseTokenBody(raw);
        if (!tok) continue;
        const binding = subjectBindings.get(tok);
        if (!binding) continue;
        if (binding.id === "full-team" && ref.requiresPlayer) {
          // player_card.player_id is a bare int — assigning "full-team" (the empty-roster
          // fallback) would fail backend validation on every subsequent save.
          drop = true;
          report.substitutions.push("Omitted a player card — your team has no players to fill its slot.");
          continue;
        }
        ref.set(binding.id);
      }
    }

    if (drop) {
      report.droppedWidgetIds.push(w.id);
      continue;
    }

    // Global name pass: {MET_1}/{PL_1}/… in titles/name fields → resolved names.
    const resolved = JSON.parse(
      JSON.stringify(config).replace(TOKEN_RE, (whole, kind, n) => {
        const name = nameByToken.get(`${kind}_${n}`);
        return name != null ? JSON.stringify(name).slice(1, -1) : whole;
      }),
    ) as Record<string, unknown>;

    keptWidgets.push({ id: w.id, type: w.type, spec: resolved as unknown as ComponentSpec, meta: undefined });
    keptIds.add(w.id);
  }

  const payload: ServerDashboardPayload = {
    id: template.id,
    name: ctx.dashboardName,
    spec_version: template.spec_version,
    widgets: keptWidgets,
    layout: template.layout.filter((l) => keptIds.has(l.i)),
  };

  return { payload, report };
}

/** Identity-keep-first, then category-aware positional substitution. */
function bindMetrics(
  template: TemplateFileV2,
  ctx: TemplateResolveContext,
  report: ResolutionReport,
): Map<string, MetricBinding | null> {
  const bindings = new Map<string, MetricBinding | null>();

  const byCategory = new Map<MetricCategory, string[]>();
  for (const tokenBody of Object.keys(template.slots.metrics)) {
    const cat = template.slots.metrics[tokenBody].category;
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(tokenBody);
  }

  for (const [cat, tokenBodies] of byCategory) {
    tokenBodies.sort((a, b) => tokenIndex(a) - tokenIndex(b));
    const pool = ctx.metricsByCategory[cat] ?? [];
    const byId = new Map(pool.map((m) => [m.id, m]));
    const used = new Set<string>();

    // pass 1 — identity keep
    const deferred: string[] = [];
    for (const tb of tokenBodies) {
      const slot = template.slots.metrics[tb];
      const hit = byId.get(slot.key);
      if (hit) { bindings.set(tb, { id: hit.id, name: hit.name }); used.add(hit.id); }
      else deferred.push(tb);
    }

    // pass 2 — positional fill from remaining, then wrap-reuse
    const remaining = pool.filter((m) => !used.has(m.id));
    let ri = 0;
    let wrap = 0;
    for (const tb of deferred) {
      const slot = template.slots.metrics[tb];
      if (pool.length === 0) {
        bindings.set(tb, null);
        report.substitutions.push(`Omitted a widget using "${slot.name}" — your team has no ${cat} metrics.`);
        continue;
      }
      let pick = remaining[ri++];
      if (!pick) { pick = pool[wrap % pool.length]; wrap++; report.substitutions.push(`Reused "${pick.name}" (fewer ${cat} metrics than the template needs).`); }
      bindings.set(tb, { id: pick.id, name: pick.name });
      if (pick.id !== slot.key) report.substitutions.push(`Substituted "${slot.name}" → "${pick.name}".`);
    }
  }

  return bindings;
}

/** Positional resolution against the team roster; empty pool → full-team. */
function bindSubjects(
  template: TemplateFileV2,
  ctx: TemplateResolveContext,
  report: ResolutionReport,
): Map<string, SubjectBinding> {
  const bindings = new Map<string, SubjectBinding>();

  for (const tokenBody of Object.keys(template.slots.subjects)) {
    const parsed = parseToken(`{${tokenBody}}`);
    if (!parsed || parsed.kind === "MET") continue;
    const kind: SubjectKind = SUBJECT_KIND_BY_PREFIX[parsed.kind as "PL" | "POS" | "GRP"];
    const pool = ctx.subjects[kind] ?? [];

    if (pool.length === 0) {
      bindings.set(tokenBody, { id: "full-team", name: "Full Team" });
      report.substitutions.push(`No ${kind}s configured — a ${kind} slot fell back to Full Team.`);
      continue;
    }
    const entity = pool[(parsed.n - 1) % pool.length];
    bindings.set(tokenBody, { id: `${SUBJECT_ID_PREFIX[kind]}${entity.id}`, name: entity.name });
  }

  return bindings;
}

function tokenIndex(tokenBody: string): number {
  const m = /_(\d+)$/.exec(tokenBody);
  return m ? Number(m[1]) : 0;
}

/** If the value is exactly a single token like "{MET_1}", return its body ("MET_1"). */
function parseTokenBody(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const m = /^\{(MET|PL|POS|GRP)_(\d+)\}$/.exec(v);
  return m ? `${m[1]}_${m[2]}` : null;
}
